import { defineStore } from 'pinia';
import { isInInbox, resolveUserFolder } from '@/utils/emailFolders';
import { latestIncomingEmail, hasVisibleAttachments, visibleAttachments, groupEmailsByConversation, externalParticipants } from '@/utils/email';
import { ref, computed } from 'vue';
import {
  collection,
  query,
  orderBy,
  limit,
  where,
  onSnapshot,
  getDocs,
  doc,
  getDoc,
  getCountFromServer,
  documentId,
  Timestamp,
  startAfter,
  type Unsubscribe,
} from 'firebase/firestore';
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider';
import { useCompanyStore } from './company';
import { useEmailAuthStore } from './auth_email';
import type { Email, EmailAddress } from '@/types';

/**
 * Store dedicado para leer emails desde Firestore en tiempo real.
 * Adapta los documentos de Firestore (fromAddress, receivedAt, bodyText...)
 * al formato Email que los componentes del inbox esperan (from.email, receivedDateTime, bodyPreview...).
 */
// DEBUG: Set to true to bypass email auth and show all company emails
const DEBUG_SKIP_EMAIL_AUTH = false;

const LOAD_MORE_BATCH_SIZE = 50

// Cuántos correos pide cada listener al abrir el buzón.
const INITIAL_LISTENER_LIMIT = 150

// Techo de correos vivos en memoria. Al alcanzarlo se deja de paginar: para ir
// más atrás está la búsqueda, que consulta Firestore sin traerse la bandeja.
const MAX_EMAILS_IN_MEMORY = 600

// Tope de correos que puede traer la expansión de hilos por lote de 30 conversaciones.
const THREAD_EXPANSION_LIMIT = 100

// Cuerpos completos que se conservan tras abrir un hilo (ver stripForList).
const BODY_CACHE_LIMIT = 30

/**
 * Versión liviana del documento, que es la que se queda en memoria mientras el
 * buzón está abierto.
 *
 * En producción el correo promedio pesa 47 KB y el 91% son dos campos que la
 * lista no muestra: `bodyText` (29 KB) y los metadatos de `attachments` (14 KB,
 * ids de Gmail y URLs de Storage con token). Guardarlos por cada correo de la
 * bandeja era lo que agotaba la memoria de la pestaña. Se conservan el preview
 * y el conteo, y el contenido se vuelve a pedir al abrir el hilo (hydrateBodies).
 */
export function stripForList(docData: any, docId: string): any {
  const { bodyHtml, bodyText, attachments, ...rest } = docData;

  return {
    ...rest,
    id: docId,
    bodyPreview:
      docData.bodyPreview ||
      (bodyText ? String(bodyText).substring(0, 200).replace(/\s+/g, ' ').trim() : ''),
    bodyContentType: bodyHtml ? 'html' : 'text',
    hasAttachments: hasVisibleAttachments(docData),
    attachmentCount: visibleAttachments(attachments, bodyHtml).length,
    bodyStripped: true,
  };
}

export const useFirestoreEmailStore = defineStore('firestoreEmail', () => {
  const db = FirebaseProvider.getInstance().getFirestore();
  const companyStore = useCompanyStore();

  // ============================================================================
  // STATE
  // ============================================================================

  const inboxEmails = ref<Email[]>([]);
  const sentEmails = ref<Email[]>([]);
  const currentEmail = ref<Email | null>(null);
  const rawEmailsMap = ref<Map<string, any>>(new Map());
  const searchRawEmailsMap = ref<Map<string, any>>(new Map());
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const totalEmails = ref(0);
  const isListenerActive = ref(false);
  const isLoadingMore = ref(false);
  const hasMoreEmails = ref(true);
  const olderEmailsMap = ref<Map<string, any>>(new Map());
  // Cuerpos y adjuntos de los últimos hilos abiertos. Fuera de los refs a
  // propósito: no se renderiza, solo alimenta a firestoreDocToEmail cuando el
  // listener reconstruye la lista y hay que devolver el correo ya hidratado.
  const bodyCache = new Map<string, { content: string; contentType: 'text' | 'html'; attachments: any[] }>();
  const needsReauth = ref(false);
  const reauthReason = ref<string | null>(null);
  // El refresh tuvo que renunciar a permisos (contactos, OneDrive): el correo
  // funciona, pero conviene reconectar para recuperarlos.
  const needsScopeUpgrade = ref(false);

  // El backend responde 401 EMAIL_REAUTH_REQUIRED cuando la conexión con el
  // buzón caducó o fue revocada (services/api.ts lo convierte en este evento).
  // Antes ese 401 se trataba como JWT vencido y terminaba cerrando la sesión
  // sin decir que había que reconectar el correo.
  if (typeof window !== 'undefined') {
    window.addEventListener('email:reauth-required', (event: Event) => {
      needsReauth.value = true;
      reauthReason.value = (event as CustomEvent).detail?.message || null;
    });
  }

  // Conteo exacto de recibidos no leídos del inbox (vía getCountFromServer),
  // usado para el badge de Inbox cuando el proveedor es Gmail (Graph devuelve 0).
  const inboxUnreadCount = ref(0);

  // Combinar emails recibidos y enviados, sin duplicados
  const emails = computed(() => {
    const seenIds = new Set<string>();
    const combined: Email[] = [];
    for (const e of inboxEmails.value) {
      if (!seenIds.has(e.id)) {
        seenIds.add(e.id);
        combined.push(e);
      }
    }
    for (const e of sentEmails.value) {
      if (!seenIds.has(e.id)) {
        seenIds.add(e.id);
        combined.push(e);
      }
    }
    return combined;
  });

  let unsubscribe: Unsubscribe | null = null;
  let unsubscribe2: Unsubscribe | null = null;
  let unsubscribe3: Unsubscribe | null = null;
  let unsubscribeConnection: Unsubscribe | null = null;
  let lastExpansionSignature = '';
  // Correos que esta pestaña acaba de mandar a la papelera (KILA-463). El backend
  // responde antes de escribir el soft delete en Firestore y los correos de
  // "Cargar más" no tienen listener: sin esto el correo seguía en la bandeja, o
  // volvía a salir con el siguiente snapshot, hasta recargar.
  const locallyTrashedIds = new Set<string>();

  // ============================================================================
  // COMPUTED
  // ============================================================================

  // Un correo movido o archivado desde Kila queda con `folder` en Firestore:
  // sigue en la colección (asociaciones, IA) pero ya no es de la bandeja.
  const inboxVisibleEmails = computed(() => emails.value.filter(isInInbox));

  const unreadCount = computed(() =>
    inboxVisibleEmails.value.filter(email => !email.isRead).length
  );

  const sortedEmails = computed(() =>
    [...inboxVisibleEmails.value].sort((a, b) =>
      new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime()
    )
  );

  // Une por conversationId o por asunto normalizado con un participante externo
  // en común (KILA-469 lo hace solo por asunto en el Buzón del embarque): el
  // conversationId es propio de cada buzón y un hilo sincronizado por varios
  // buzones de la compañía salía en varias filas.
  const conversationGroups = computed(() => {
    const groups = new Map<string, Email[]>();
    const mailboxEmail = useEmailAuthStore().userEmail;
    groupEmailsByConversation(inboxVisibleEmails.value, {
      participants: email => externalParticipants(email, mailboxEmail),
    }).forEach(group => {
      groups.set(group.key, group.emails);
    });
    return groups;
  });

  const conversations = computed(() => {
    const convArray: Array<{ latestEmail: Email; emails: Email[]; count: number }> = [];

    // El buzón conectado: un hilo que solo tiene correos míos vive en Enviados,
    // no en la bandeja, y mi respuesta no reemplaza al último recibido en la
    // vista previa. Los correos propios siguen en `emails` para que el hilo se
    // lea completo al abrirlo. Los enviados por una regla automática (KILA-260)
    // tampoco abren fila, ni a quien va en Para o CC; se suma a la regla anterior
    // sin alterarla, y la respuesta del cliente (sin la marca) entra normal.
    const mailboxEmail = useEmailAuthStore().userEmail;

    conversationGroups.value.forEach((groupEmails) => {
      const latestEmail = latestIncomingEmail(groupEmails.filter(e => !e.automaticSend), mailboxEmail);
      if (latestEmail) {
        convArray.push({
          latestEmail,
          emails: groupEmails,
          count: groupEmails.length,
        });
      }
    });

    return convArray.sort(
      (a, b) =>
        new Date(b.latestEmail.receivedDateTime).getTime() -
        new Date(a.latestEmail.receivedDateTime).getTime()
    );
  });

  // ============================================================================
  // ADAPTADOR: Firestore Doc → Email (formato UI)
  // ============================================================================

  function firestoreDocToEmail(docData: any, docId: string): Email {
    const receivedAt = docData.receivedAt instanceof Timestamp
      ? docData.receivedAt.toDate()
      : docData.receivedAt ? new Date(docData.receivedAt) : new Date();

    const sentAt = docData.sentAt instanceof Timestamp
      ? docData.sentAt.toDate()
      : docData.sentAt ? new Date(docData.sentAt) : undefined;

    const fromAddress: EmailAddress = {
      email: docData.fromAddress || '',
      name: docData.fromName || docData.fromAddress || '',
    };

    const toAddresses: EmailAddress[] = (docData.toAddresses || []).map((addr: string) => ({
      email: addr,
      name: addr,
    }));

    const ccAddresses: EmailAddress[] = (docData.ccAddresses || []).map((addr: string) => ({
      email: addr,
      name: addr,
    }));

    // Un documento podado (el de la lista) no trae cuerpo: se usa el del caché
    // si el hilo ya se abrió, y si no queda vacío hasta que hydrateBodies lo traiga.
    const cachedBody = bodyCache.get(docId);
    const bodyContent = docData.bodyHtml || docData.bodyText || cachedBody?.content || '';
    const bodyContentType: 'text' | 'html' = docData.bodyHtml
      ? 'html'
      : docData.bodyText
        ? 'text'
        : cachedBody?.contentType || docData.bodyContentType || 'text';
    const bodyPreview = docData.bodyText
      ? docData.bodyText.substring(0, 200).replace(/\s+/g, ' ').trim()
      : docData.bodyPreview || '';

    const matchedPurchaseOrders = Array.isArray(docData.matchedPurchaseOrders)
      ? docData.matchedPurchaseOrders
      : undefined;

    // Array plano de IDs para consultas array-contains en Firestore
    const matchedPurchaseOrderIds = Array.isArray(docData.matchedPurchaseOrderIds)
      ? docData.matchedPurchaseOrderIds
      : matchedPurchaseOrders?.map((po: any) => po.id).filter(Boolean) || undefined;

    const hasMatch = !!(docData.matchedShipmentId || (matchedPurchaseOrders && matchedPurchaseOrders.length > 0));

    return {
      id: docId,
      conversationId: docData.conversationId || undefined,
      subject: docData.subject || '(Sin asunto)',
      from: fromAddress,
      to: toAddresses,
      cc: ccAddresses.length > 0 ? ccAddresses : undefined,
      body: {
        content: bodyContent,
        contentType: bodyContentType,
      },
      bodyLoaded: !!bodyContent,
      bodyPreview,
      attachments: (docData.attachments || cachedBody?.attachments || []).map((att: any) => ({
        id: att.id || att.filename,
        name: att.filename || att.name || 'archivo',
        contentType: att.contentType || 'application/octet-stream',
        size: att.size || 0,
        url: att.downloadUrl || att.url || undefined,
        isInline: att.isInline || false,
        contentId: att.contentId || undefined,
      })),
      hasAttachments: hasVisibleAttachments(docData),
      isRead: docData.isRead ?? false,
      isDraft: docData.isDraft ?? false,
      importance: docData.importance || 'normal',
      receivedDateTime: receivedAt.toISOString(),
      sentDateTime: sentAt?.toISOString(),
      flag: docData.flag || { flagStatus: 'notFlagged' },
      // Carpeta de ESTE usuario: archivar o mover es personal (folderByUser),
      // la papelera es compartida. Ver utils/emailFolders.resolveUserFolder.
      folder: locallyTrashedIds.has(docId) ? 'trash' : resolveUserFolder(docData, useEmailAuthStore().mailboxUserId),
      needsAction: docData.needsAction || null,
      matchedShipmentId: docData.matchedShipmentId || undefined,
      matchedShipmentIdNumber: docData.matchedShipmentIdNumber || undefined,
      matchedPurchaseOrders,
      matchedPurchaseOrderIds,
      hasMatch,
      automaticSend: docData.automaticSend ?? null,
    };
  }

  // ============================================================================
  // MÉTODOS
  // ============================================================================

  function getCompanyId(): string | null {
    const authStore = useEmailAuthStore();
    return authStore.companyId || companyStore.selectedCompanyId || null;
  }

  /**
   * Cuenta en Firestore los correos recibidos no leídos del usuario (inbox),
   * de forma exacta y sin descargar documentos (getCountFromServer).
   * Requiere el índice compuesto emails: (toAddresses CONTAINS, isRead ASC).
   */
  async function fetchInboxUnreadCount() {
    const companyId = getCompanyId();
    const userEmail = useEmailAuthStore().user?.user.email;
    if (!companyId || !userEmail) return;

    try {
      const emailsCol = collection(db, 'Companies', companyId, 'emails');
      const unreadQuery = query(
        emailsCol,
        where('toAddresses', 'array-contains', userEmail),
        where('isRead', '==', false)
      );
      const snapshot = await getCountFromServer(unreadQuery);
      inboxUnreadCount.value = snapshot.data().count;
    } catch (err: any) {
      console.error('❌ [FirestoreEmail] Error al contar no leídos:', err);
    }
  }

  /**
   * Expande los emails del inbox con correos enviados del mismo hilo (conversationId).
   * Esto permite ver hilos completos incluyendo las respuestas enviadas por el usuario.
   */
  async function expandWithSentEmails(companyId: string, currentInboxEmails: Email[]) {
    try {
      const existingIds = new Set(currentInboxEmails.map(e => e.id));
      const conversationIds = [...new Set(
        currentInboxEmails.map(e => e.conversationId).filter(Boolean)
      )] as string[];

      // Se llama desde cada uno de los tres listeners: si las conversaciones de
      // la bandeja son las mismas de la vez anterior, repetir las consultas solo
      // gasta lecturas y genera basura (marcar un correo como leído las disparaba todas).
      const signature = conversationIds.join('|');
      if (signature === lastExpansionSignature) return;
      lastExpansionSignature = signature;

      if (conversationIds.length === 0) {
        sentEmails.value = [];
        return;
      }

      const emailsRef = collection(db, 'Companies', companyId, 'emails');
      const foundSentEmails: Email[] = [];

      // Firestore limita 'in' a 30 valores
      const batchSize = 30;
      for (let i = 0; i < conversationIds.length; i += batchSize) {
        const batch = conversationIds.slice(i, i + batchSize);
        // Con `limit`: sin él, un lote de 30 conversaciones largas se traía
        // cientos de correos completos en cada actualización del listener.
        const convQuery = query(emailsRef, where('conversationId', 'in', batch), limit(THREAD_EXPANSION_LIMIT));
        const convSnapshot = await getDocs(convQuery);

        for (const docSnap of convSnapshot.docs) {
          if (!existingIds.has(docSnap.id)) {
            existingIds.add(docSnap.id);
            const data = stripForList(docSnap.data(), docSnap.id);
            rawEmailsMap.value.set(docSnap.id, data);
            foundSentEmails.push(firestoreDocToEmail(data, docSnap.id));
          }
        }
      }

      sentEmails.value = foundSentEmails;
    } catch (err) {
      console.warn('[FirestoreEmail] Error expandiendo hilos con enviados:', err);
    }
  }

  /** Los más recientes hasta el techo: lo que sobra se suelta para que el GC lo recoja. */
  function capToMemoryLimit(docs: any[]): any[] {
    return docs.length > MAX_EMAILS_IN_MEMORY ? docs.slice(0, MAX_EMAILS_IN_MEMORY) : docs;
  }

  function rebuildInboxEmails() {
    const allData = new Map([...olderEmailsMap.value, ...rawEmailsMap.value]);
    const sorted = capToMemoryLimit([...allData.values()]
      .sort((a, b) => (b.receivedAt?.toMillis?.() ?? 0) - (a.receivedAt?.toMillis?.() ?? 0)));

    const newRawMap = new Map<string, any>();
    const updatedEmails = sorted.map(data => {
      newRawMap.set(data.id, data);
      return firestoreDocToEmail(data, data.id);
    });

    rawEmailsMap.value = newRawMap;
    inboxEmails.value = updatedEmails;
    totalEmails.value = updatedEmails.length;
  }

  async function loadMoreEmails() {
    if (isLoadingMore.value || !hasMoreEmails.value) return;

    // Techo alcanzado: seguir apilando páginas es lo que terminaba tumbando la
    // pestaña. De acá en adelante se va más atrás con la búsqueda.
    if (inboxEmails.value.length >= MAX_EMAILS_IN_MEMORY) {
      hasMoreEmails.value = false;
      return;
    }

    const companyId = getCompanyId();
    if (!companyId) return;

    const emailAuthStore = useEmailAuthStore();
    const userEmail = emailAuthStore.user?.user.email;
    if (!DEBUG_SKIP_EMAIL_AUTH && !userEmail) return;

    isLoadingMore.value = true;

    try {
      const allRawDocs = [...rawEmailsMap.value.values(), ...olderEmailsMap.value.values()];
      if (allRawDocs.length === 0) {
        isLoadingMore.value = false;
        return;
      }

      let oldestTimestamp: Timestamp | null = null;
      for (const raw of allRawDocs) {
        const ts = raw.receivedAt;
        if (ts instanceof Timestamp) {
          if (!oldestTimestamp || ts.toMillis() < oldestTimestamp.toMillis()) {
            oldestTimestamp = ts;
          }
        }
      }

      if (!oldestTimestamp) {
        hasMoreEmails.value = false;
        isLoadingMore.value = false;
        return;
      }

      const emailsCol = collection(db, 'Companies', companyId, 'emails');

      const q1 = query(
        emailsCol,
        where('syncedFromEmail', '==', userEmail),
        orderBy('receivedAt', 'desc'),
        startAfter(oldestTimestamp),
        limit(LOAD_MORE_BATCH_SIZE)
      );
      const q2 = query(
        emailsCol,
        where('toAddresses', 'array-contains', userEmail),
        orderBy('receivedAt', 'desc'),
        startAfter(oldestTimestamp),
        limit(LOAD_MORE_BATCH_SIZE)
      );

      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

      snap1.docs.forEach(d => {
        olderEmailsMap.value.set(d.id, stripForList(d.data(), d.id));
      });
      snap2.docs.forEach(d => {
        olderEmailsMap.value.set(d.id, stripForList(d.data(), d.id));
      });

      hasMoreEmails.value = snap1.size >= LOAD_MORE_BATCH_SIZE || snap2.size >= LOAD_MORE_BATCH_SIZE;

      rebuildInboxEmails();
      await expandWithSentEmails(companyId, inboxEmails.value);
    } catch (err: any) {
      console.error('[FirestoreEmail] Error loading more emails:', err);
      error.value = err.message;
    } finally {
      isLoadingMore.value = false;
    }
  }

  /**
   * Activa el listener en tiempo real de Firestore.
   * Cada vez que llega un email nuevo o se modifica uno existente,
   * se actualiza automáticamente el array de emails.
   */
  function startRealtimeListener(emailLimit = INITIAL_LISTENER_LIMIT) {
    const companyId = getCompanyId();
    if (!companyId) {
      console.error('❌ [FirestoreEmail] No hay companyId para activar listener');
      error.value = 'No hay compañía seleccionada';
      return;
    }

    // Evitar duplicar listeners
    if (unsubscribe) unsubscribe();
    if (unsubscribe2) unsubscribe2();
    if (unsubscribe3) unsubscribe3();

    isLoading.value = true;
    error.value = null;
    olderEmailsMap.value.clear();
    locallyTrashedIds.clear();
    hasMoreEmails.value = true;

    const emailAuthStore = useEmailAuthStore();
    const userEmail = emailAuthStore.user?.user.email;

    if (!DEBUG_SKIP_EMAIL_AUTH && !userEmail) {
      console.error('❌ [FirestoreEmail] No hay email de usuario para filtrar emails');
      error.value = 'No hay usuario autenticado';
      isLoading.value = false;
      return;
    }

    const emailsCol = collection(db, 'Companies', companyId, 'emails');

    if (DEBUG_SKIP_EMAIL_AUTH || !userEmail) {
      unsubscribe = onSnapshot(
        query(emailsCol, orderBy('receivedAt', 'desc'), limit(emailLimit)),
        async (snapshot) => {
          rawEmailsMap.value.clear();
          snapshot.docs.forEach(d => rawEmailsMap.value.set(d.id, stripForList(d.data(), d.id)));
          rebuildInboxEmails();
          await expandWithSentEmails(companyId!, inboxEmails.value);
          isLoading.value = false;
        },
        (err) => { console.error('❌ [FirestoreEmail] Error listener:', err); error.value = err.message; isLoading.value = false; }
      );
      return;
    }

    const syncedResults = new Map<string, any>();
    const toResults = new Map<string, any>();
    const ccResults = new Map<string, any>();

    const mergeAndUpdate = async () => {
      const listenerMerged = new Map([...syncedResults, ...toResults, ...ccResults]);
      const allData = new Map([...olderEmailsMap.value, ...listenerMerged]);
      const sorted = capToMemoryLimit([...allData.values()]
        .sort((a, b) => (b.receivedAt?.toMillis?.() ?? 0) - (a.receivedAt?.toMillis?.() ?? 0)));

      const newRawMap = new Map<string, any>();
      const updatedEmails = sorted.map(data => {
        newRawMap.set(data.id, data);
        return firestoreDocToEmail(data, data.id);
      });

      rawEmailsMap.value = newRawMap;
      inboxEmails.value = updatedEmails;
      totalEmails.value = updatedEmails.length;
      isLoading.value = false;
      isListenerActive.value = true;

      await expandWithSentEmails(companyId, updatedEmails);
    };

    // Listener 1: correos sincronizados desde la cuenta del usuario
    unsubscribe = onSnapshot(
      query(emailsCol, where('syncedFromEmail', '==', userEmail), orderBy('receivedAt', 'desc'), limit(emailLimit)),
      (snapshot) => {
        syncedResults.clear();
        snapshot.docs.forEach(d => syncedResults.set(d.id, stripForList(d.data(), d.id)));
        mergeAndUpdate();
      },
      (err) => {
        console.error('❌ [FirestoreEmail] Error en listener syncedFromEmail:', err);
        error.value = err.message;
        isLoading.value = false;
        isListenerActive.value = false;
      }
    );

    // Listener 2: correos donde el usuario es destinatario
    unsubscribe2 = onSnapshot(
      query(emailsCol, where('toAddresses', 'array-contains', userEmail), orderBy('receivedAt', 'desc'), limit(emailLimit)),
      (snapshot) => {
        toResults.clear();
        snapshot.docs.forEach(d => toResults.set(d.id, stripForList(d.data(), d.id)));
        mergeAndUpdate();
      },
      (err) => {
        console.error('❌ [FirestoreEmail] Error en listener toAddresses:', err);
        error.value = err.message;
        isLoading.value = false;
        isListenerActive.value = false;
      }
    );

    // Listener 3: correos donde el usuario está en CC
    unsubscribe3 = onSnapshot(
      query(emailsCol, where('ccAddresses', 'array-contains', userEmail), orderBy('receivedAt', 'desc'), limit(emailLimit)),
      (snapshot) => {
        ccResults.clear();
        snapshot.docs.forEach(d => ccResults.set(d.id, stripForList(d.data(), d.id)));
        mergeAndUpdate();
      },
      (err) => {
        console.error('❌ [FirestoreEmail] Error en listener ccAddresses:', err);
      }
    );

    // Listener de estado de conexion: detecta si el usuario necesita re-autenticarse
    const connectionsCol = collection(db, 'Companies', companyId, 'emailConnections');
    unsubscribeConnection = onSnapshot(
      query(connectionsCol, where('email', '==', userEmail), limit(1)),
      (snapshot) => {
        if (!snapshot.empty) {
          const connData = snapshot.docs[0].data();
          needsReauth.value = connData.needsReauth === true;
          reauthReason.value = connData.reauthReason || null;
          needsScopeUpgrade.value = connData.needsScopeUpgrade === true;
        }
      },
      (err) => {
        console.error('❌ [FirestoreEmail] Error en listener emailConnection:', err);
      }
    );
  }

  /**
   * Detiene el listener en tiempo real.
   */
  function stopRealtimeListener() {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    if (unsubscribe2) {
      unsubscribe2();
      unsubscribe2 = null;
    }
    if (unsubscribe3) {
      unsubscribe3();
      unsubscribe3 = null;
    }
    if (unsubscribeConnection) {
      unsubscribeConnection();
      unsubscribeConnection = null;
    }
    isListenerActive.value = false;

    // Salir del buzón tiene que devolver la memoria: sin esto, la bandeja
    // completa seguía retenida el resto de la sesión aunque nadie la mirara.
    rawEmailsMap.value.clear();
    olderEmailsMap.value.clear();
    searchRawEmailsMap.value.clear();
    bodyCache.clear();
    locallyTrashedIds.clear();
    inboxEmails.value = [];
    sentEmails.value = [];
    totalEmails.value = 0;
    hasMoreEmails.value = true;
    lastExpansionSignature = '';
  }

  /** Guarda el cuerpo de un correo abierto, soltando el más viejo al pasar del tope. */
  function rememberBody(id: string, content: string, contentType: 'text' | 'html', attachments: any[]) {
    bodyCache.delete(id);
    bodyCache.set(id, { content, contentType, attachments });
    while (bodyCache.size > BODY_CACHE_LIMIT) {
      const oldest = bodyCache.keys().next().value;
      if (oldest === undefined) break;
      bodyCache.delete(oldest);
    }
  }

  /**
   * Trae el cuerpo y los adjuntos de los correos de un hilo, que la lista no
   * guarda (ver stripForList), y los deja en los objetos que ya está mostrando
   * la vista. Mientras llega, el hilo muestra el preview.
   */
  async function hydrateBodies(emailsToHydrate: Email[]): Promise<void> {
    const companyId = getCompanyId();
    if (!companyId || emailsToHydrate.length === 0) return;

    const pending: Email[] = [];
    for (const email of emailsToHydrate) {
      if (email.bodyLoaded) continue;
      const cached = bodyCache.get(email.id);
      if (cached) {
        email.body = { content: cached.content, contentType: cached.contentType };
        email.attachments = cached.attachments;
        email.bodyLoaded = true;
      } else {
        pending.push(email);
      }
    }

    if (pending.length === 0) return;

    try {
      const emailsRef = collection(db, 'Companies', companyId, 'emails');
      for (let i = 0; i < pending.length; i += 30) {
        const chunk = pending.slice(i, i + 30);
        const snapshot = await getDocs(
          query(emailsRef, where(documentId(), 'in', chunk.map(e => e.id)))
        );
        const byId = new Map(snapshot.docs.map(d => [d.id, d.data()]));

        for (const email of chunk) {
          const data = byId.get(email.id);
          // Un correo que ya no está en Firestore no se reintenta en cada apertura.
          if (!data) {
            email.bodyLoaded = true;
            continue;
          }
          const content = data.bodyHtml || data.bodyText || '';
          const contentType: 'text' | 'html' = data.bodyHtml ? 'html' : 'text';
          const attachments = firestoreDocToEmail(data, email.id).attachments || [];

          rememberBody(email.id, content, contentType, attachments);
          email.body = { content, contentType };
          email.attachments = attachments;
          email.bodyLoaded = true;
        }
      }
    } catch (err) {
      console.error('❌ [FirestoreEmail] Error hidratando cuerpos:', err);
    }
  }

  /**
   * Obtiene un email específico por ID desde Firestore.
   */
  async function fetchEmailById(id: string) {
    const companyId = getCompanyId();
    if (!companyId) return null;

    try {
      const emailRef = doc(db, 'Companies', companyId, 'emails', id);
      const emailSnap = await getDoc(emailRef);

      if (!emailSnap.exists()) {
        console.warn(`❌ [FirestoreEmail] Email no encontrado: ${id}`);
        return null;
      }

      const email = firestoreDocToEmail(emailSnap.data(), emailSnap.id);
      if (email.body.content) {
        rememberBody(email.id, email.body.content, email.body.contentType, email.attachments || []);
      }
      currentEmail.value = email;
      return email;
    } catch (err: any) {
      console.error('❌ [FirestoreEmail] Error fetching email:', err);
      return null;
    }
  }

  /**
   * Recarga manual — reinicia el listener.
   */
  async function fetchEmails() {
    stopRealtimeListener();
    startRealtimeListener();
  }

  /**
   * Busca emails por texto (filtrado local).
   */
  function searchEmails(queryText: string) {
    // El filtrado se hace local ya que el listener ya carga todos los emails
  }

  function setCurrentEmail(email: Email | null) {
    currentEmail.value = email;
  }

  /**
   * Saca de la bandeja, sin esperar al listener, los correos que el backend ya
   * confirmó como borrados. Se mantiene hasta que el listener se reinicia
   * (recargar o volver a entrar a la bandeja), y en ese momento manda lo que diga Firestore.
   */
  function markEmailsTrashed(ids: string[]): void {
    if (ids.length === 0) return;
    ids.forEach(id => locallyTrashedIds.add(id));
    const gone = new Set(ids);
    for (const email of [...inboxEmails.value, ...sentEmails.value]) {
      if (gone.has(email.id)) email.folder = 'trash';
    }
  }

  /**
   * Obtiene los datos raw de Firestore para un email (incluye parsed, matchedShipmentId, etc.)
   */
  function getRawEmailData(emailId: string): any | null {
    return rawEmailsMap.value.get(emailId) || searchRawEmailsMap.value.get(emailId) || null;
  }

  function setRawEmailData(emailId: string, data: any) {
    searchRawEmailsMap.value.set(emailId, data);
  }

  /**
   * Vacía los documentos de la búsqueda anterior. Se guardan completos porque el
   * filtro por texto mira el cuerpo, así que sin esto cada búsqueda apilaba
   * hasta 500 correos más que ya nadie miraba.
   */
  function clearSearchRawData() {
    searchRawEmailsMap.value.clear();
  }

  function clearError() {
    error.value = null;
  }

  /**
   * Cleanup al destruir el store.
   */
  function $dispose() {
    stopRealtimeListener();
  }

  return {
    // State
    emails,
    currentEmail,
    isLoading,
    error,
    totalEmails,
    isListenerActive,
    isLoadingMore,
    hasMoreEmails,
    needsReauth,
    reauthReason,
    needsScopeUpgrade,
    inboxUnreadCount,
    // Computed
    unreadCount,
    sortedEmails,
    conversationGroups,
    conversations,
    // Methods
    fetchInboxUnreadCount,
    startRealtimeListener,
    loadMoreEmails,
    stopRealtimeListener,
    fetchEmails,
    fetchEmailById,
    searchEmails,
    setCurrentEmail,
    getRawEmailData,
    setRawEmailData,
    markEmailsTrashed,
    clearSearchRawData,
    hydrateBodies,
    clearError,
    $dispose,
  };
});
